import React from "react";
import { Text, FileUpload, type FileObjectType } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import * as styles from "../styles_vanilla.css";

export type ConfigFileUploadFieldProps = {
  /** Display label for the field (e.g., "Units CSV File") */
  label: string;

  /** Unique identifier for the FileUpload key (e.g., "units") */
  keyPrefix: string;

  /** Description of what the file contains (e.g., "units configuration") */
  description: string;

  /** Callback when files are selected */
  onFileSelected: (files: FileObjectType[]) => void;

  /** Currently selected files */
  initialFiles: FileObjectType[];

  /** Whether the field is disabled (typically during submission) */
  isDisabled: boolean;

  /** Global file upload key for forcing re-render */
  fileUploadKey: number;
};

/**
 * Reusable file upload field component for CSV configuration files.
 * Used in AdvancedPermissionsSection for consistent file upload UI.
 */
export const ConfigFileUploadField: React.FC<ConfigFileUploadFieldProps> = ({
  label,
  keyPrefix,
  description,
  onFileSelected,
  initialFiles,
  isDisabled,
  fileUploadKey,
}) => (
  <div className={styles.fileUploadItem}>
    <Text variant="strong-md" color={Colors.Neutral900} className={styles.fileLabel}>
      {label} (Optional)
    </Text>
    <FileUpload
      key={`${keyPrefix}-${fileUploadKey}`}
      uploadMessage="Drop the file here or"
      acceptedFileTypes={[".csv"]}
      onFileSelected={onFileSelected}
      initialFiles={initialFiles}
      isFullWidth
      canAllowMultiple={false}
      maxAcceptedFilesToDisplay={1}
      isDisabled={isDisabled}
    />
    <Text variant="regular-sm" color={Colors.Neutral600} className={styles.fieldDescription}>
      Upload a CSV file containing {description}. Only .CSV format is supported.
    </Text>
  </div>
);
